/**
 * AIProgressBar — wyswietla postep operacji AI w real-time.
 *
 * Nasluchuje WebSocket event "ai_progress" z backendu.
 * Pokazuje: progress bar + label (generic, bez szczegolow pipelineu).
 * Automatycznie znika po 3s od zakonczenia (stage === total).
 *
 * Usage:
 *   <AIProgressBar />
 *   Umieszczony w AppShell — widoczny globalnie gdy AI pracuje.
 */

import { useState, useEffect, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { Loader2, Brain, CheckCircle2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cancelAITask } from "@/lib/api";

// ETA table: czas (s) na kazdy etap per-module, oparty o Load Test #6 pod 10 concurrent.
// Uzywane do pokazywania pozostalego czasu — "Etap 3/5 (~90s pozostalo)".
const STAGE_DURATIONS_SEC = {
  contract_analyze: {
    1: 15,   // Wstepna analiza
    2: 25,   // Wyszukiwanie podstaw prawnych
    3: 120,  // Ocena kazdej klauzuli (Claude Opus) — NAJDLUZSZY
    4: 15,   // Weryfikacja cytatow
    5: 90,   // Kontrola jakosci (GPT QG)
  },
  generate: {
    1: 20,
    2: 25,
    3: 90,
    4: 15,
    5: 20,
  },
  advice_deep: {
    1: 20,
    2: 30,
    3: 60,
    4: 60,
    5: 20,
  },
  // 2026-04-27: pre-analysis stages dla advice/contract z plikami.
  // Wczesniej user widzial "martwe okno" przez OCR (~10-30s/plik). Teraz
  // backend emituje progress per plik (advice_prep) lub jednorazowo + per plik
  // (contract_batch_prep).
  advice_prep: {
    1: 15,   // Ekstrakcja tekstu z pliku 1 (PDF / OCR)
    2: 15,
    3: 15,
    4: 15,
    5: 15,
    6: 15,
    7: 15,
    8: 15,
    9: 15,
    10: 15,
  },
  contract_batch_prep: {
    1: 5,    // Wgrywanie
    2: 20,   // Ekstrakcja pliku 1
    3: 20,
    4: 20,
    5: 20,
    6: 20,
    7: 20,
    8: 20,
    9: 20,
    10: 20,
    11: 20,
  },
  studio_chat: {
    1: 2,    // Wczytywanie materiałów projektu (Mongo read)
    2: 12,   // Wyszukiwanie podstaw prawnych (ChromaDB + Borg WK + KG równolegle)
    3: 25,   // Redakcja odpowiedzi (Claude)
  },
  legal_search: {
    1: 8,
    2: 15,
    3: 12,
  },
  legal_deep: {
    1: 20,
    2: 60,
    3: 90,
    4: 30,
  },
  due_diligence: {
    1: 15,
    2: 60,
    3: 30,
  },
};

function computeETA(module, currentStage, totalStages) {
  const durations = STAGE_DURATIONS_SEC[module];
  if (!durations) return null;
  let remainingSec = 0;
  for (let s = currentStage + 1; s <= totalStages; s++) {
    remainingSec += durations[s] || 30;
  }
  // Aktualny etap — polowa czasu
  if (currentStage >= 1 && currentStage <= totalStages) {
    remainingSec += (durations[currentStage] || 30) / 2;
  }
  return Math.round(remainingSec);
}

function formatETA(sec) {
  if (sec < 60) return `~${sec}s pozostalo`;
  const min = Math.round(sec / 60);
  return `~${min} ${min === 1 ? "minuta" : min < 5 ? "minuty" : "minut"} pozostalo`;
}

export default function AIProgressBar() {
  const [progress, setProgress] = useState(null); // {stage, total, label, pct, module, sub_status}
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    // Listen for WebSocket messages
    const handleWsMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.type === "ai_progress") {
          const payload = data.data || data;
          setProgress(payload);
          setVisible(true);

          // Clear any hide timer
          if (hideTimer.current) clearTimeout(hideTimer.current);

          // Auto-hide 3s after completion
          if (payload.stage >= payload.total) {
            // Faza 1.5 (2026-04-27): sound feedback opt-in localStorage flag
            // Useful gdy task trwa >2 min i user przeszedl do innej zakladki.
            // Default OFF — user musi explicitly wlaczyc w Settings (TBD UI).
            try {
              if (localStorage.getItem("leslex_ai_sound") === "on") {
                // Web Audio beep — brak external audio file (SHA hash w bundle)
                // Dwa kolejne piknieci — krotkie, niskie volume, przyjemne.
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const playBeep = (freq, when) => {
                  const osc = ctx.createOscillator();
                  const gain = ctx.createGain();
                  osc.connect(gain);
                  gain.connect(ctx.destination);
                  osc.frequency.value = freq;
                  gain.gain.setValueAtTime(0, ctx.currentTime + when);
                  gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + when + 0.02);
                  gain.gain.linearRampToValueAtTime(0, ctx.currentTime + when + 0.18);
                  osc.start(ctx.currentTime + when);
                  osc.stop(ctx.currentTime + when + 0.2);
                };
                playBeep(800, 0);
                playBeep(1100, 0.12);
              }
            } catch {
              // Audio context może nie działać (Safari pre-user-gesture, etc.)
            }

            hideTimer.current = setTimeout(() => {
              setVisible(false);
              setProgress(null);
            }, 3000);
          }
        } else if (data.type === "ai_substatus") {
          // 2026-04-27: granular sub_status update bez zmiany stage
          const payload = data.data || data;
          setProgress((prev) => prev ? { ...prev, sub_status: payload.sub_status } : prev);
        }
      } catch {
        // Not JSON or not our message
      }
    };

    // Hook into existing WebSocket (created by api.js)
    const originalWs = window.__leslex_ws;
    if (originalWs) {
      originalWs.addEventListener("message", handleWsMessage);
    }

    // Also listen for custom event (fallback if WS not direct)
    const handleCustomEvent = (e) => {
      handleWsMessage({ data: JSON.stringify(e.detail) });
    };
    window.addEventListener("ai_progress", handleCustomEvent);

    return () => {
      if (originalWs) {
        originalWs.removeEventListener("message", handleWsMessage);
      }
      window.removeEventListener("ai_progress", handleCustomEvent);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const isComplete = progress && progress.stage >= progress.total;
  // 2026-04-27 (Faza 1.4): emoji per-module dla szybkiej identyfikacji wizualnej
  const moduleLabels = {
    advice_deep: "🧠 Porada poglebiona",
    generate: "✍️ Generowanie pisma",
    review: "🔍 Recenzja",
    advice_quick: "⚡ Porada szybka",
    tax_optimization: "💰 Optymalizacja podatkowa",
    contract_analyze: "📜 Analiza umowy",
    iterate: "♻️ Iteracja pisma",
    auto_fix: "🔧 Auto-poprawa pisma",
    advice_prep: "📂 Przygotowanie porady — czytanie plikow",
    contract_batch_prep: "📂 Przygotowanie analizy — czytanie plikow",
    studio_chat: "💬 Chat Studio",
    legal_search: "⚖️ Wyszukiwanie prawne",
    legal_deep: "🔬 Deep research prawny",
    due_diligence: "🏢 Due diligence",
  };

  const etaSec = progress && !isComplete
    ? computeETA(progress.module, progress.stage, progress.total)
    : null;

  return (
    <AnimatePresence>
      {visible && progress && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[400px] max-w-[90vw]"
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl p-4">
            <div className="flex items-start gap-3 mb-2">
              {isComplete ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {isComplete ? "Gotowe!" : progress.label}
                </p>
                {/* 2026-04-27: detailed sub_status — co konkretnie AI robi
                    (np. "Pobieram art. 415 k.c.", "Analiza klauzuli 3 z 12") */}
                {progress.sub_status && !isComplete && (
                  <p className="text-xs text-primary/80 italic mt-0.5 truncate animate-pulse">
                    {progress.sub_status}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {moduleLabels[progress.module] || "AI"} — Etap {progress.stage}/{progress.total}
                  {etaSec !== null && (
                    <span className="ml-2 text-primary/80 font-medium">
                      · {formatETA(etaSec)}
                    </span>
                  )}
                </p>
              </div>
              {/* 2026-04-27: Cancel button (UX 2026) — pojawia sie tylko gdy task_id w payloadzie */}
              {!isComplete && progress.task_id ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await cancelAITask(progress.task_id);
                      setVisible(false);
                      setProgress(null);
                    } catch (err) {
                      // Silent fail — task moze juz byc skonczony
                      console.warn("Cancel failed:", err);
                    }
                  }}
                  className="text-muted-foreground/60 hover:text-destructive shrink-0 mt-0.5 transition-colors"
                  title="Anuluj operacje"
                  aria-label="Anuluj operacje AI"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <Brain className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
              )}
            </div>
            <Progress
              value={isComplete ? 100 : progress.pct}
              className="h-2"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
